import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Check, ArrowLeft, FileText, Download, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import SEOHead, { generateBreadcrumbSchema } from '../components/SEOHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_LEAD_FEE = 0;
const DEFAULT_REV_SHARE = 0;

function NumberInput({ id, label, suffix, value, onChange, min = 0, max = 5000, step = 1, helperText, testId }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-semibold text-white mb-1.5">
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type="number"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={onChange}
          data-testid={testId}
          className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-[#00F0FF] pr-12"
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 text-sm">{suffix}</span>
      </div>
      {helperText ? (
        <p className="text-xs text-neutral-500 mt-1">{helperText}</p>
      ) : null}
    </div>
  );
}

export default function BuildPartnersApply() {
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState({
    company_name: '',
    website_url: '',
    contact_name: '',
    contact_email: '',
    contact_role: '',
    services_built: '',
    typical_project_size_usd: '',
    proposed_per_lead_fee_usd: DEFAULT_LEAD_FEE,
    proposed_rev_share_pct: DEFAULT_REV_SHARE,
    agreed_to_terms: false,
    notes: '',
  });

  const update = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm({ ...form, [k]: v });
  };
  const updateNum = (k) => (e) =>
    setForm({ ...form, [k]: e.target.value === '' ? '' : Number(e.target.value) });

  const submit = async (e) => {
    e.preventDefault();
    if (form.services_built.trim().length < 20) {
      toast.error('Tell us a bit more about what you build (at least 20 characters).');
      return;
    }
    if (!form.agreed_to_terms) {
      toast.error('Please confirm you agree to the AI Build Partner agreement.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        typical_project_size_usd: form.typical_project_size_usd === ''
          ? null
          : Number(form.typical_project_size_usd),
        proposed_per_lead_fee_usd: Number(form.proposed_per_lead_fee_usd),
        proposed_rev_share_pct: Number(form.proposed_rev_share_pct),
      };
      const res = await axios.post(`${API_URL}/api/build-partner-applications`, payload);
      setSuccess(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-black text-white px-6 py-20">
        <SEOHead
          title="Application received — AI Build Partners | theailedger.ai"
          description="Your AI Build Partner application is in. We review within 2 business days."
        />
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-16 h-16 rounded-full bg-[#00FF94]/15 border border-[#00FF94]/40 flex items-center justify-center mx-auto mb-6">
            <Check className="w-7 h-7 text-[#00FF94]" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-3" data-testid="build-partner-success-heading">
            Application received
          </h1>
          <p className="text-neutral-300 text-base sm:text-lg leading-relaxed mb-2">
            {success.message}
          </p>
          <p className="text-neutral-500 text-sm mb-10">
            Reference: <code className="text-[#00F0FF]">{success.application_id}</code>
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#00F0FF] hover:text-white"
            data-testid="build-partner-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to theailedger.ai
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white px-6 py-16">
      <SEOHead
        title="Apply: AI Build Partner Program | theailedger.ai"
        description="B2B AI dev agencies — apply to be listed on theailedger.ai. No listing fee, just a simple referral split on leads we send you."
        path="/build-partners/apply"
        keywords="AI dev agency referral, AI build partner, B2B AI agency, AI development referral program, theailedger build partner"
        schema={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'Service',
              name: 'AI Build Partners referral program',
              serviceType: 'B2B AI dev agency referral program',
              url: 'https://theailedger.ai/build-partners/apply',
              description: 'Referral program for B2B AI development agencies. No listing fee; partners and theailedger.ai agree case-by-case on a per-lead fee plus Year-1 revenue share.',
              provider: {
                '@type': 'Organization',
                name: 'theailedger.ai',
                url: 'https://theailedger.ai',
              },
              offers: {
                '@type': 'Offer',
                price: '0',
                priceCurrency: 'USD',
                availability: 'https://schema.org/InStock',
                url: 'https://theailedger.ai/build-partners/apply',
                description: 'No upfront cost. Per-lead fee + agreed Year-1 revenue share on closed deals, negotiated case-by-case.',
              },
            },
            generateBreadcrumbSchema([
              { name: 'Home', path: '/' },
              { name: 'AI Build Partners', path: '/build-partners/apply' },
            ]),
          ],
        }}
      />
      <div className="max-w-3xl mx-auto">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-neutral-400 hover:text-white text-sm mb-6"
          data-testid="build-partner-back-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Link>

        <div className="inline-block px-3 py-1 rounded-full bg-[#00F0FF]/10 border border-[#00F0FF]/30 text-[#00F0FF] text-xs font-bold tracking-wider uppercase mb-4">
          AI Build Partners Program
        </div>
        <h1 className="text-3xl sm:text-5xl font-bold mb-3 tracking-tight" data-testid="build-partner-apply-heading">
          Send us qualified buyers. We send you revenue.
        </h1>
        <p className="text-neutral-300 text-base sm:text-lg leading-relaxed mb-8 max-w-2xl">
          A partner program for B2B AI dev agencies and build studios. No
          listing fee — just a simple referral split on the deals we send your way.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
          <a
            href="/legal/build-partners-overview.pdf"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-3 p-4 bg-white/[0.03] border border-white/10 rounded-xl hover:border-[#00F0FF]/40 transition"
            data-testid="build-partner-pdf-overview"
          >
            <FileText className="w-5 h-5 text-[#00F0FF] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-white">Program overview (PDF)</div>
              <div className="text-xs text-neutral-400 truncate">Forward this to your team</div>
            </div>
            <Download className="w-4 h-4 text-neutral-400" />
          </a>
          <a
            href="/legal/build-partner-agreement-template.pdf"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-3 p-4 bg-white/[0.03] border border-white/10 rounded-xl hover:border-[#00F0FF]/40 transition"
            data-testid="build-partner-pdf-agreement"
          >
            <FileText className="w-5 h-5 text-[#00F0FF] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-white">Agreement template (PDF)</div>
              <div className="text-xs text-neutral-400 truncate">12-month, auto-renewing</div>
            </div>
            <Download className="w-4 h-4 text-neutral-400" />
          </a>
        </div>

        <form onSubmit={submit} className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold mb-1.5">Company name</label>
              <input
                type="text"
                required
                value={form.company_name}
                onChange={update('company_name')}
                data-testid="build-partner-input-company"
                className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-[#00F0FF]"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1.5">Website</label>
              <input
                type="url"
                required
                placeholder="https://"
                value={form.website_url}
                onChange={update('website_url')}
                data-testid="build-partner-input-website"
                className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-[#00F0FF]"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold mb-1.5">Your name</label>
              <input
                type="text"
                required
                value={form.contact_name}
                onChange={update('contact_name')}
                data-testid="build-partner-input-name"
                className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-[#00F0FF]"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1.5">Email</label>
              <input
                type="email"
                required
                value={form.contact_email}
                onChange={update('contact_email')}
                data-testid="build-partner-input-email"
                className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-[#00F0FF]"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1.5">Role (optional)</label>
              <input
                type="text"
                value={form.contact_role}
                onChange={update('contact_role')}
                placeholder="Founder, BD lead…"
                data-testid="build-partner-input-role"
                className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-[#00F0FF]"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1.5">What kinds of AI apps do you build?</label>
            <textarea
              required
              rows={4}
              value={form.services_built}
              onChange={update('services_built')}
              placeholder="e.g. We build production-grade RAG chatbots and AI agents for mid-market SaaS companies. Typical engagement is 8–12 weeks, $40K–$120K. Stack: Next.js + Python + Pinecone + GPT-5.2…"
              data-testid="build-partner-input-services"
              className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-[#00F0FF] resize-none"
            />
            <p className="text-xs text-neutral-500 mt-1">
              If approved, this text will be used as your marketing copy on theailedger.ai. Make it count.
            </p>
          </div>

          <NumberInput
            id="typical-project-size"
            label="Typical project size (optional)"
            suffix="USD"
            value={form.typical_project_size_usd}
            onChange={updateNum('typical_project_size_usd')}
            min={0}
            max={10000000}
            step={5000}
            helperText="Helps us decide which leads to route to you. Leave blank if it varies."
            testId="build-partner-input-project-size"
          />

          <div className="bg-white/[0.03] border border-[#00F0FF]/20 rounded-xl p-5">
            <div className="text-xs font-bold tracking-wider uppercase text-[#00F0FF] mb-1">
              Your proposed terms
            </div>
            <p className="text-sm text-neutral-300 mb-4">
              Per-lead fee and Year-1 revenue share are agreed case-by-case.
              Propose what works for your agency below — we'll confirm or
              counter during review and the final numbers go into the
              Build Partner agreement.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <NumberInput
                id="per-lead-fee"
                label="Per qualified lead"
                suffix="USD"
                value={form.proposed_per_lead_fee_usd}
                onChange={updateNum('proposed_per_lead_fee_usd')}
                min={0}
                max={5000}
                step={10}
                helperText="Your proposal (USD)"
                testId="build-partner-input-lead-fee"
              />
              <NumberInput
                id="rev-share"
                label="Revenue share (Year 1)"
                suffix="%"
                value={form.proposed_rev_share_pct}
                onChange={updateNum('proposed_rev_share_pct')}
                min={0}
                max={100}
                step={0.5}
                helperText="Your proposal (% of closed deals)"
                testId="build-partner-input-rev-share"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1.5">Anything else? (optional)</label>
            <textarea
              rows={3}
              value={form.notes}
              onChange={update('notes')}
              placeholder="Special terms, existing relationship with us, anything else…"
              data-testid="build-partner-input-notes"
              className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-[#00F0FF] resize-none"
            />
          </div>

          <label className="flex items-start gap-3 p-4 bg-white/[0.03] border border-white/10 rounded-xl cursor-pointer">
            <input
              type="checkbox"
              checked={form.agreed_to_terms}
              onChange={update('agreed_to_terms')}
              data-testid="build-partner-input-agree"
              className="mt-1 w-4 h-4 accent-[#00F0FF]"
            />
            <span className="text-sm text-neutral-300 leading-relaxed">
              I have read and agree to the{' '}
              <a
                href="/legal/build-partner-agreement-template.pdf"
                target="_blank"
                rel="noreferrer"
                className="text-[#00F0FF] hover:underline inline-flex items-center gap-1"
              >
                AI Build Partner Referral Agreement
                <ExternalLink className="w-3 h-3" />
              </a>
              . I understand the final agreement will reflect the per-lead fee
              and revenue share I proposed above (or as negotiated with Theailedger LLC during review).
            </span>
          </label>

          <button
            type="submit"
            disabled={submitting}
            data-testid="build-partner-submit-button"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3 bg-[#00F0FF] text-black font-bold rounded-xl hover:bg-[#00F0FF]/90 transition disabled:opacity-50"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {submitting ? 'Submitting…' : 'Submit application'}
          </button>

          <p className="text-xs text-neutral-500">
            We review within 2 business days and email you at <span className="text-neutral-300">{form.contact_email || 'the address you provide'}</span> with a decision.
          </p>
        </form>
      </div>
    </div>
  );
}
